'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { getUserPreferences, upsertUserPreferences, UserPreferences } from '@/lib/supabase/preferencesService';
import { trackEvent } from '@/lib/analytics';

const categories = [
  { id: 'all', name: 'All', icon: '📰' },
  { id: 'tech', name: 'Technology', icon: '💻' },
  { id: 'business', name: 'Business', icon: '💼' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
  { id: 'politics', name: 'Politics', icon: '🏛️' },
  { id: 'entertainment', name: 'Entertainment', icon: '🎬' },
  { id: 'health', name: 'Health', icon: '🏥' },
  { id: 'science', name: 'Science', icon: '🔬' },
];

const countries = [
  { code: 'WORLD', name: 'World', flag: '🌍' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
];

const refreshOptions = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
];

const summaryLengthOptions = [
  { value: 50, label: '50 words' },
  { value: 75, label: '75 words' },
  { value: 100, label: '100 words' },
  { value: 150, label: '150 words' },
];

export default function PreferencesPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [subscribedCategories, setSubscribedCategories] = useState<string[]>(['all']);
  const [selectedCountries, setSelectedCountries] = useState<string[]>(['WORLD']);
  const [refreshFrequency, setRefreshFrequency] = useState(60);
  const [summaryLength, setSummaryLength] = useState(100);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [pushNotifications, setPushNotifications] = useState(false);

  const demoUserId = 'demo-user-123';

  useEffect(() => {
    loadPreferences();
    trackEvent('preferences_page_view', {});
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const prefs = await getUserPreferences(demoUserId);
      
      if (prefs) {
        setSubscribedCategories(prefs.subscribedCategories || ['all']);
        setSelectedCountries(prefs.selectedCountries || ['WORLD']);
        setRefreshFrequency(prefs.refreshFrequency || 60);
        setSummaryLength(prefs.summaryLength || 100);
        setNotificationsEnabled(prefs.notificationsEnabled ?? true);
        setEmailNotifications(prefs.emailNotifications ?? false);
        setPushNotifications(prefs.pushNotifications ?? false);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const preferences: Partial<UserPreferences> = {
        userId: demoUserId,
        subscribedCategories,
        selectedCountries,
        refreshFrequency,
        summaryLength,
        notificationsEnabled,
        emailNotifications,
        pushNotifications,
      };

      await upsertUserPreferences(demoUserId, preferences);
      setSuccess('Preferences saved successfully!');
      trackEvent('preferences_saved', { userId: demoUserId });
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    if (categoryId === 'all') {
      setSubscribedCategories(['all']);
    } else {
      const filtered = subscribedCategories.filter(c => c !== 'all');
      if (filtered.includes(categoryId)) {
        const updated = filtered.filter(c => c !== categoryId);
        setSubscribedCategories(updated.length > 0 ? updated : ['all']);
      } else {
        setSubscribedCategories([...filtered, categoryId]);
      }
    }
  };

  const toggleCountry = (countryCode: string) => {
    if (selectedCountries.includes(countryCode)) {
      const updated = selectedCountries.filter(c => c !== countryCode);
      setSelectedCountries(updated.length > 0 ? updated : ['WORLD']);
    } else {
      setSelectedCountries([...selectedCountries, countryCode]);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading preferences...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link href="/homepage" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <AppImage
                src="/assets/images/intagg_logo-01-1770559550357.png"
                alt="Intaggs logo"
                width={40}
                height={40}
                className="rounded-md"
              />
              <div>
                <h1 className="font-heading font-bold text-xl text-foreground">Intaggs</h1>
                <p className="text-xs text-muted-foreground">Preferences</p>
              </div>
            </Link>
            <Link
              href="/homepage"
              className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              <Icon name="ArrowLeftIcon" size={18} />
              <span className="text-sm font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className="font-heading font-bold text-4xl text-foreground mb-2">News Preferences</h2>
          <p className="text-muted-foreground">Customize your personalized news feed experience</p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
            <Icon name="ExclamationCircleIcon" size={20} className="text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500 rounded-lg flex items-center gap-3">
            <Icon name="CheckCircleIcon" size={20} className="text-green-500" />
            <p className="text-sm text-green-500">{success}</p>
          </div>
        )}

        {/* Category Subscriptions */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Squares2X2Icon" size={24} className="text-primary" />
            <h3 className="font-heading font-bold text-xl text-foreground">Category Subscriptions</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Select the news categories you want to follow</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => toggleCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  subscribedCategories.includes(category.id)
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-background text-foreground border-border hover:border-primary hover:bg-secondary/50'
                }`}
              >
                <span className="text-lg">{category.icon}</span>
                <span className="font-medium text-sm">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Country Selections */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="GlobeAltIcon" size={24} className="text-primary" />
            <h3 className="font-heading font-bold text-xl text-foreground">Country Selections</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Choose countries to get news from</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => toggleCountry(country.code)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  selectedCountries.includes(country.code)
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-background text-foreground border-border hover:border-primary hover:bg-secondary/50'
                }`}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="font-medium text-sm">{country.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Refresh Frequency */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="ArrowPathIcon" size={24} className="text-primary" />
            <h3 className="font-heading font-bold text-xl text-foreground">Refresh Frequency</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">How often should we update your news feed?</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {refreshOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setRefreshFrequency(option.value)}
                className={`px-4 py-3 rounded-lg border transition-all ${
                  refreshFrequency === option.value
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-background text-foreground border-border hover:border-primary hover:bg-secondary/50'
                }`}
              >
                <span className="font-medium text-sm">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Summary Length */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="DocumentTextIcon" size={24} className="text-primary" />
            <h3 className="font-heading font-bold text-xl text-foreground">Summary Length</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Preferred length for article summaries</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {summaryLengthOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSummaryLength(option.value)}
                className={`px-4 py-3 rounded-lg border transition-all ${
                  summaryLength === option.value
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-background text-foreground border-border hover:border-primary hover:bg-secondary/50'
                }`}
              >
                <span className="font-medium text-sm">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-card border border-border rounded-lg p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="BellIcon" size={24} className="text-primary" />
            <h3 className="font-heading font-bold text-xl text-foreground">Notification Settings</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Manage how you receive news updates</p>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div>
                <p className="font-medium text-foreground">Enable Notifications</p>
                <p className="text-sm text-muted-foreground">Receive updates about breaking news</p>
              </div>
              <button
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationsEnabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div>
                <p className="font-medium text-foreground">Email Notifications</p>
                <p className="text-sm text-muted-foreground">Get news summaries via email</p>
              </div>
              <button
                onClick={() => setEmailNotifications(!emailNotifications)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  emailNotifications ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div>
                <p className="font-medium text-foreground">Push Notifications</p>
                <p className="text-sm text-muted-foreground">Instant alerts for important news</p>
              </div>
              <button
                onClick={() => setPushNotifications(!pushNotifications)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  pushNotifications ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    pushNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="font-medium">Saving...</span>
              </>
            ) : (
              <>
                <Icon name="CheckIcon" size={18} />
                <span className="font-medium">Save Preferences</span>
              </>
            )}
          </button>
        </div>
      </div>
    </main>
  );
}